// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { fullViewportHeight } from "@store/Framework/Common/Utilities/siteBanners";
import SignedOutHeader, {
    SignedOutHeaderPresentationProps,
    SignedOutHeaderProps,
} from "@store/Library/Components/SignedOut/SignedOutHeader";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, HTMLAttributes, PropsWithChildren, ReactNode } from "react";
import { css } from "styled-components";

const mainPanelWidth = 617;

export interface SignedOutSplitLayoutPresentationProps extends ExpandStyles<SignedOutSplitLayoutStyles> {}

export interface SignedOutSplitLayoutProps extends SignedOutSplitLayoutPresentationProps, HTMLAttributes<HTMLElement> {
    headerProps?: Omit<SignedOutHeaderProps, keyof SignedOutHeaderPresentationProps>;
    sideContent?: ReactNode;
}

export interface SignedOutSplitLayoutStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    mainWrapper?: InjectableCss;
    sideWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    signedOutHeader?: SignedOutHeaderPresentationProps;
}

export const signedOutSplitLayoutStyles: SignedOutSplitLayoutStyles = {
    wrapper: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            min-height: ${fullViewportHeight};
            background-image: linear-gradient(160.36deg, #21604b 0.86%, ${getColor("tertiary")} 97.15%);
            background-attachment: fixed;

            ${breakpoint("lg")} {
                &::after {
                    content: "";
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: calc(100% - ${pxToRem(mainPanelWidth)});
                    background-color: ${getColor("common.background")};
                }
            }
        `,
    },
    innerWrapper: {
        css: css`
            ${siteWidthStyles([])}
            position: relative;
            z-index: 1;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            width: 100%;

            ${breakpoint("lg")} {
                flex-direction: row-reverse;
            }
        `,
    },
    sideWrapper: {
        css: css`
            display: flex;
            flex-grow: 1;
            align-items: center;
            justify-content: center;
            padding: ${pxToRem(32, 20)};

            ${breakpoint("sm")} {
                padding: ${pxToRem(32, 40)};
            }

            ${breakpoint("md")} {
                padding: ${pxToRem(50, 40)};
            }

            ${breakpoint("lg")} {
                position: sticky;
                top: 0;
                align-self: flex-start;
                min-height: ${fullViewportHeight};
                padding: ${pxToRem(50, 40, 50, 45)};
            }
        `,
    },
    mainWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(32)};
            width: 100%;
            padding: ${pxToRem(24, 20, 45)};
            background-color: ${getColor("common.background")};

            ${breakpoint("sm")} {
                padding: ${pxToRem(24, 40, 45)};
            }

            ${breakpoint("md")} {
                gap: ${pxToRem(40)};
                padding: ${pxToRem(45, 40)};
            }

            ${breakpoint("lg")} {
                flex: 0 0 ${pxToRem(mainPanelWidth)};
                padding: ${pxToRem(45, 45, 45, 40)};
            }
        `,
    },
};

const SignedOutSplitLayout: FC<PropsWithChildren<SignedOutSplitLayoutProps>> = ({
    children,
    extendedStyles,
    headerProps,
    overridedStyles,
    sideContent,
    ...htmlAttributes
}) => {
    const styles = useExpandProps(signedOutSplitLayoutStyles, extendedStyles, overridedStyles);

    return (
        <StyledWrapper {...styles.wrapper} {...htmlAttributes}>
            <StyledWrapper {...styles.innerWrapper}>
                <StyledWrapper {...styles.mainWrapper}>
                    <SignedOutHeader {...styles.signedOutHeader} {...headerProps} />
                    <StyledWrapper {...styles.contentWrapper}>{children}</StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.sideWrapper}>{sideContent}</StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default SignedOutSplitLayout;
